import { useEffect, useState } from "react";

function ProfileCollegeSelect({
    email = "",
    value = "",
    setValue = () => {},
}) {
    const [loading, setLoading] = useState(false);
    const [schools, setSchools] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [input, setInput] = useState(value || "");
    const [touched, setTouched] = useState(false);

    const emailDomain = (email || "").toLowerCase().split("@")[1] || "";
    const lockInput = emailDomain.endsWith(".edu");

    useEffect(() => {
        const controller = new AbortController();

        (async () => {
            try {
                setLoading(true);
                const res = await fetch("/static/wuad.json", {
                    signal: controller.signal,
                });

                const data = res.ok ? await res.json() : [];
                const names = data
                    .map((row) => (row.name || "").trim())
                    .filter(Boolean);

                setSchools(names);
            } catch (err) {
                if (err.name !== "AbortError") {
                    console.error("ProfileCollegeSelect fetch error:", err);
                }
            } finally {
                setLoading(false);
            }
        })();

        return () => controller.abort();
    }, []);

    useEffect(() => {
        if (touched) return;
        setInput(value || "");
    }, [value, touched]);

    const isValidSchool = (v) => {
        return schools.includes((v || "").trim());
    };

    const buildSuggestions = (v) => {
        const q = (v || "").trim().toLowerCase();
        if (!q) return [];

        return schools
            .filter((name) => name.toLowerCase().includes(q))
            .slice(0, 25);
    };

    const commitSelection = (schoolName) => {
        const v = (schoolName || "").trim();
        setInput(v);
        setValue(v);
        setFiltered([]);
    };

    const handleBlur = () => {
        if (lockInput) return;

        if (!input.trim()) {
            setFiltered([]);
            return;
        }

        if (!isValidSchool(input)) {
            setInput(value || "");
            setFiltered([]);
        }
    };

    return (
        <div style={{ position: "relative" }}>
            <input
                className="form-input"
                type="text"
                aria-label="Search and select your college"
                value={loading ? "..." : input}
                placeholder={
                    lockInput
                        ? "College (locked for .edu emails)"
                        : "Search and select your college"
                }
                readOnly={lockInput}
                onChange={(event) => {
                    if (lockInput) return;

                    setTouched(true);
                    const val = event.target.value;
                    setInput(val);
                    setFiltered(buildSuggestions(val));
                }}
                onFocus={() => {
                    if (lockInput) return;
                    setTouched(true);
                    setFiltered(buildSuggestions(input));
                }}
                onBlur={handleBlur}
                onKeyDown={(event) => {
                    if (lockInput) return;

                    if (event.key === "Enter") {
                        event.preventDefault();
                        if (filtered[0]) {
                            commitSelection(filtered[0]);
                        }
                    }
                }}
            />

            {lockInput && (
                <div
                    style={{
                        marginTop: 6,
                        fontSize: 12,
                        opacity: 0.8,
                    }}
                >
                    This field is locked for <b>.edu</b> emails.
                </div>
            )}

            {!lockInput && input.trim().length > 0 && !isValidSchool(input) && (
                <div
                    style={{
                        marginTop: 6,
                        fontSize: 12,
                        color: "var(--danger-color)",
                    }}
                >
                    Please select a valid school from the dropdown.
                </div>
            )}

            {!lockInput && filtered.length > 0 && (
                <ul
                    style={{
                        listStyle: "none",
                        padding: 0,
                        margin: 0,
                        backgroundColor: "white",
                        border: "1px solid var(--primary-color)",
                        maxHeight: "200px",
                        overflowY: "auto",
                        position: "absolute",
                        width: "100%",
                        zIndex: 200,
                    }}
                >
                    {filtered.map((name, i) => (
                        <li
                            key={`${name}-${i}`}
                            className="college-suggestion-item"
                            onMouseDown={(event) => {
                                event.preventDefault();
                                commitSelection(name);
                            }}
                            style={{
                                padding: "8px 10px",
                                cursor: "pointer",
                            }}
                        >
                            {name}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export { ProfileCollegeSelect };
